/*
 * Changes:
 * - Added 'use client' directive for Next.js 13+ compatibility
 * - File contains client-side features: React hooks, setInterval, and axios HTTP requests
 */

'use client'

import {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'

const DEFAULT_TOKEN_REFRESH_INTERVAL = 1000 * 60 * 5

const OAuthCredentialsRefreshProvider = ({
  children,
  clientId,
  isEnabled = true,
  handleOauthCredentialsRefreshSuccess = null,
  handleOauthCredentialsRefreshError = null,
  oauthCredentials,
  tokenRefreshInterval = DEFAULT_TOKEN_REFRESH_INTERVAL,
  tokenRefreshUrl = null
}) => {
  const oauthCredentialsRefreshIntervalIdRef = useRef(null)
  const lastRefreshTimeRef = useRef(null)

  // Effect to detect token changes from other tabs using localStorage events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'oauthCredentials' && e.newValue) {
        try {
          const newCredentials = JSON.parse(e.newValue)

          // Compare with current Jotai state
          if (newCredentials && oauthCredentials.accessToken) {
            const newExpiresAt = newCredentials.expiresAt
            const currentExpiresAt = oauthCredentials.expiresAt

            // If localStorage has newer tokens, update the Jotai state
            if (newExpiresAt && currentExpiresAt && newExpiresAt !== currentExpiresAt) {
              // Update the Jotai state with the new tokens from localStorage
              if (handleOauthCredentialsRefreshSuccess) {
                handleOauthCredentialsRefreshSuccess({
                  accessToken: newCredentials.accessToken,
                  refreshToken: newCredentials.refreshToken,
                  expiresAt: newCredentials.expiresAt
                })
              }

              // Reset the interval to prevent duplicate refresh attempts
              if (oauthCredentialsRefreshIntervalIdRef.current) {
                clearInterval(oauthCredentialsRefreshIntervalIdRef.current)
                oauthCredentialsRefreshIntervalIdRef.current = null
              }
            }
          }
        } catch (error) {
          // Intentionally empty - gracefully handle refresh interval errors
        }
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange)

      return () => {
        window.removeEventListener('storage', handleStorageChange)
      }
    }

    return () => {}
  }, [oauthCredentials.accessToken, oauthCredentials.expiresAt, handleOauthCredentialsRefreshSuccess])

  useEffect(() => {
    function resetInterval() {
      clearInterval(oauthCredentialsRefreshIntervalIdRef.current)
      oauthCredentialsRefreshIntervalIdRef.current = null
    }

    const isAuthenticated = !!oauthCredentials.accessToken

    if (isEnabled && isAuthenticated && !oauthCredentialsRefreshIntervalIdRef.current) {
      oauthCredentialsRefreshIntervalIdRef.current = setInterval(async () => {
        try {
          const startTime = Date.now()

          const response = await fetch(tokenRefreshUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include', // Include cookies for session
            body: JSON.stringify({
              refresh_token: oauthCredentials.refreshToken,
              grant_type: 'refresh_token',
              client_id: clientId
            })
          })

          const requestTime = Date.now() - startTime

          const responseText = await response.text()

          let data
          try {
            data = JSON.parse(responseText)
          } catch (e) {
            console.error('Failed to parse OAuth response as JSON:', e)
            throw new Error(`Invalid JSON response: ${responseText}`)
          }

          if (!response.ok) {
            // Check for specific HTTP status codes that indicate race conditions
            if (response.status === 401 && data.message && data.message.includes('already been used')) {
              console.warn(`[OAuthDebug] 401 with race condition message - another tab refreshed the token`)
              throw new Error('RACE_CONDITION: ' + data.message)
            }
            throw new Error(data.message || `HTTP ${response.status}`)
          }

          if (!data) {
            console.error(`[OAuthDebug] No data in response!`)
            throw new Error('No data in refresh token response')
          }

          const updatedOauthCredentials = {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            expiresAt: data.expires_at
          }

          if (handleOauthCredentialsRefreshSuccess) {
            handleOauthCredentialsRefreshSuccess(updatedOauthCredentials)
          }
        } catch (e) {
          console.error('Token refresh failed:', e.message)

          // Check if this is a race condition error (token already used by another tab)
          if (e.message && (e.message.includes('RACE_CONDITION:') || e.message.includes('already been used') || e.message.includes('already been used or is invalid'))) {
            // Force a check for updated tokens from localStorage with multiple attempts
            const checkForUpdatedTokens = (attempt = 1, maxAttempts = 5) => {
              try {
                if (typeof window === 'undefined' || !window.localStorage) {
                  return
                }

                const storedData = localStorage.getItem('oauthCredentials')

                if (storedData) {
                  const storedCredentials = JSON.parse(storedData)

                  if (storedCredentials && storedCredentials.accessToken && storedCredentials.accessToken !== oauthCredentials.accessToken) {
                    if (handleOauthCredentialsRefreshSuccess) {
                      handleOauthCredentialsRefreshSuccess({
                        accessToken: storedCredentials.accessToken,
                        refreshToken: storedCredentials.refreshToken,
                        expiresAt: storedCredentials.expiresAt
                      })
                    }
                    return // Success, stop checking
                  }
                }

                // If no updated tokens found and we haven't reached max attempts, try again
                if (attempt < maxAttempts) {
                  setTimeout(() => checkForUpdatedTokens(attempt + 1, maxAttempts), attempt * 100)
                } else {
                  console.warn(`[OAuthDebug] No updated tokens found after ${maxAttempts} attempts`)
                }
              } catch (error) {
                // Intentionally empty - gracefully handle token check errors
              }
            }

            // Start checking immediately and then with increasing delays
            checkForUpdatedTokens()

            // Don't immediately log out the user for race conditions
            return
          }

          if (handleOauthCredentialsRefreshError) {
            handleOauthCredentialsRefreshError(e)
          }
        }
      }, tokenRefreshInterval)
    } else if (!isAuthenticated && !!oauthCredentialsRefreshIntervalIdRef.current) {
      resetInterval()
    }

    return () => {
      resetInterval()
    }
  }, [isEnabled, oauthCredentials.accessToken, oauthCredentials.refreshToken, clientId, tokenRefreshUrl, tokenRefreshInterval])

  return children
}

OAuthCredentialsRefreshProvider.propTypes = {
  clientId: PropTypes.string.isRequired,
  isEnabled: PropTypes.bool,
  handleOauthCredentialsRefreshSuccess: PropTypes.func,
  handleOauthCredentialsRefreshError: PropTypes.func,
  oauthCredentials: PropTypes.shape({
    accessToken: PropTypes.string,
    refreshToken: PropTypes.string,
    expiresAt: PropTypes.string
  }).isRequired,
  tokenRefreshInterval: PropTypes.number,
  tokenRefreshUrl: PropTypes.string
}

export {OAuthCredentialsRefreshProvider}
